import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, AlertCircle, Building2 } from "lucide-react";

const AUSTRALIAN_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export const StepCompanyBorrower = ({ formData, updateFormData, prefillData }) => {
  const [positions, setPositions] = useState(formData.company_borrower.positions || [
    { name: "", is_director: false, is_secretary: false, is_shareholder: false }
  ]);
  // APP-FORM-FIX (2026-07-25) — root cause of "prefill doesn't work": this
  // Select is intentionally uncontrolled (no `value` prop — it's a one-shot
  // "quick fill" action, not a persistent field), but Radix's Select still
  // tracks its own internal selected value. onValueChange only fires when
  // that internal value CHANGES, so re-picking the SAME company a second
  // time (e.g. after editing fields and wanting to re-apply the prefill, or
  // returning to this step later) silently does nothing — no error, no
  // visual feedback, it just looks broken. Bumping `key` forces Radix to
  // remount the Select after every pick, clearing its internal state so the
  // next selection (even an identical one) always fires.
  const [prefillResetKey, setPrefillResetKey] = useState(0);

  const handleCompanyChange = (field, value) => {
    updateFormData("company_borrower", {
      ...formData.company_borrower,
      [field]: value,
    });
  };

  const selectPrefillCompany = (companyName) => {
    setPrefillResetKey((k) => k + 1);
    if (!prefillData?.companies) return;
    const match = prefillData.companies.find(c => c.name === companyName);
    if (match && match.data) {
      const d = match.data;
      updateFormData("company_borrower", {
        ...formData.company_borrower,
        company_name: d.company_name || "",
        acn_abn: d.acn_abn || "",
        company_email: d.company_email || "",
        industry_type: d.industry_type || "",
        contact_number: d.contact_number || "",
        annual_income: d.annual_income || 0,
        is_trustee: d.is_trustee || false,
        trustee_name: d.trustee_name || "",
        is_smsf_trustee: d.is_smsf_trustee || false,
        registered_address: d.registered_address || formData.company_borrower.registered_address,
        positions: d.positions || formData.company_borrower.positions,
      });
      if (d.positions?.length) {
        setPositions(d.positions);
      }
      // APP-FORM-FIX (2026-07-25) — explicit confirmation. Previously this
      // was a silent local-state update with no toast; combined with the
      // no-backend-autosave gap (see LoanApplicationForm.jsx), a client who
      // prefilled then navigated away before Continue/Save Progress would
      // see the prefill "not work" (nothing to confirm it happened, and
      // nothing persisted it).
      toast.success(`Prefilled company details from "${companyName}"`);
    }
  };

  const handleAddressChange = (field, value) => {
    updateFormData("company_borrower", {
      ...formData.company_borrower,
      registered_address: {
        ...formData.company_borrower.registered_address,
        [field]: value,
      },
    });
  };

  const handleSolvencyChange = (field, value) => {
    updateFormData("solvency_enquiries", {
      ...formData.solvency_enquiries,
      [field]: value,
    });
  };

  const handlePositionChange = (index, field, value) => {
    const newPositions = [...positions];
    newPositions[index] = { ...newPositions[index], [field]: value };
    setPositions(newPositions);
    handleCompanyChange("positions", newPositions);
  };

  const addPosition = () => {
    const newPositions = [...positions, { name: "", is_director: false, is_secretary: false, is_shareholder: false }];
    setPositions(newPositions);
    handleCompanyChange("positions", newPositions);
  };

  const removePosition = (index) => {
    if (positions.length > 1) {
      const newPositions = positions.filter((_, i) => i !== index);
      setPositions(newPositions);
      handleCompanyChange("positions", newPositions);
      toast.success("Removed — this saves automatically");
    }
  };

  const solvencyQuestions = [
    { key: "pending_litigation", label: "Does the Borrower(s) and the Guarantor(s) have any pending or past litigation matters (within the last 2 years)?" },
    { key: "unsatisfied_judgements", label: "Are there any unsatisfied judgements against the Borrower(s) and the Guarantor(s)?" },
    { key: "bankrupt_past_5_years", label: "Have the Borrower(s) and the Guarantor(s) been bankrupt in the past 5 years?" },
    { key: "credit_refused_1_year", label: "Has the Borrower(s) and the Guarantor(s) been refused by a credit provider in the last 1 year?" },
    { key: "outstanding_ato_debts", label: "Are there any outstanding debts current or otherwise due to the ATO by the Borrower(s) and the Guarantor(s)?" },
    { key: "outstanding_tax_returns", label: "Does the Borrower(s) and the Guarantor(s) have outstanding tax or BAS returns due to be lodged with the ATO?" },
    { key: "arrangement_with_creditor", label: "Has the Borrower(s) and the Guarantor(s) made any arrangement with a creditor to payout debt that is still current?" },
  ];

  return (
    <div className="space-y-10">
      {/* Company Borrower Details Section */}
      <section className="form-card rounded">
        <h3 className="section-subheader">Company Borrower Details</h3>

        {/* Prefill from previous applications */}
        {prefillData?.companies?.length > 0 && (
          <div className="mb-6 p-4 bg-[#021d49]/5 border border-[#021d49]/10 rounded">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-[#bd9a59]" />
              <Label className="text-sm font-medium text-[#021d49]">Quick Fill from Previous Applications</Label>
            </div>
            <Select key={`prefill-company-${prefillResetKey}`} onValueChange={selectPrefillCompany} data-testid="prefill-company-select">
              <SelectTrigger className="form-input" data-testid="prefill-company-trigger">
                <SelectValue placeholder="Select a previous company to prefill details..." />
              </SelectTrigger>
              <SelectContent>
                {prefillData.companies.map((c) => (
                  <SelectItem key={c.name} value={c.name} data-testid={`prefill-option-${c.name}`}>
                    {c.name} {c.data.acn_abn ? `(${c.data.acn_abn})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">Select to prefill — you can edit any field after.</p>
          </div>
        )}
        
        <div className="form-grid form-grid-2">
          <div>
            <Label className="form-label form-label-required">Company Name</Label>
            <Input
              value={formData.company_borrower.company_name}
              onChange={(e) => handleCompanyChange("company_name", e.target.value)}
              placeholder="Enter company name"
              className="form-input"
              data-testid="company-name-input"
            />
          </div>
          
          <div>
            <Label className="form-label form-label-required">ACN/ABN</Label>
            <Input
              value={formData.company_borrower.acn_abn}
              onChange={(e) => handleCompanyChange("acn_abn", e.target.value)}
              placeholder="XX XXX XXX XXX"
              className="form-input"
              data-testid="acn-abn-input"
            />
          </div>
          
          <div>
            <Label className="form-label">Industry Type</Label>
            <Input
              value={formData.company_borrower.industry_type}
              onChange={(e) => handleCompanyChange("industry_type", e.target.value)}
              placeholder="e.g., Construction, Real Estate"
              className="form-input"
              data-testid="industry-type-input"
            />
          </div>
          
          <div>
            <Label className="form-label">Company Email</Label>
            <Input
              type="email"
              value={formData.company_borrower.company_email}
              onChange={(e) => handleCompanyChange("company_email", e.target.value)}
              placeholder="company@example.com"
              className="form-input"
              data-testid="company-email-input"
            />
          </div>
          
          <div>
            <Label className="form-label">Contact Number</Label>
            <Input
              value={formData.company_borrower.contact_number}
              onChange={(e) => handleCompanyChange("contact_number", e.target.value)}
              placeholder="(XX) XXXX XXXX"
              className="form-input"
              data-testid="contact-number-input"
            />
          </div>
          
          <div>
            <Label className="form-label">Annual Company Income</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <Input
                type="number"
                value={formData.company_borrower.annual_income || ""}
                onChange={(e) => handleCompanyChange("annual_income", parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                className="form-input pl-8 text-right"
                data-testid="annual-income-input"
              />
            </div>
          </div>
        </div>

        {/* Trustee Questions */}
        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-6">
            <Label className="form-label mb-0">Is the company a trustee?</Label>
            <RadioGroup
              value={formData.company_borrower.is_trustee ? "yes" : "no"}
              onValueChange={(v) => handleCompanyChange("is_trustee", v === "yes")}
              className="flex gap-4"
              data-testid="is-trustee-radio"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="trustee-yes" />
                <Label htmlFor="trustee-yes" className="cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="trustee-no" />
                <Label htmlFor="trustee-no" className="cursor-pointer">No</Label>
              </div>
            </RadioGroup>
          </div>
          
          {formData.company_borrower.is_trustee && (
            <div className="ml-6">
              <Label className="form-label">Trustee Name</Label>
              <Input
                value={formData.company_borrower.trustee_name}
                onChange={(e) => handleCompanyChange("trustee_name", e.target.value)}
                placeholder="Enter trust name"
                className="form-input max-w-md"
                data-testid="trustee-name-input"
              />
            </div>
          )}

          <div className="flex items-center gap-6">
            <Label className="form-label mb-0">Is the company a trustee for an SMSF?</Label>
            <RadioGroup
              value={formData.company_borrower.is_smsf_trustee ? "yes" : "no"}
              onValueChange={(v) => handleCompanyChange("is_smsf_trustee", v === "yes")}
              className="flex gap-4"
              data-testid="is-smsf-trustee-radio"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="smsf-yes" />
                <Label htmlFor="smsf-yes" className="cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="smsf-no" />
                <Label htmlFor="smsf-no" className="cursor-pointer">No</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
      </section>

      {/* Positions Held Section */}
      <section className="form-card rounded">
        <div className="flex justify-between items-center mb-4">
          <h3 className="section-subheader mb-0">Positions Held</h3>
          <button
            onClick={addPosition}
            className="btn-secondary px-3 py-2 rounded text-sm flex items-center gap-1"
            data-testid="add-position-btn"
          >
            <Plus className="w-4 h-4" />
            Add Person
          </button>
        </div>

        {positions.map((position, index) => (
          <div key={index} className="p-4 bg-gray-50 rounded mb-4" data-testid={`position-${index}`}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1 mr-4">
                <Label className="form-label">Name</Label>
                <Input
                  value={position.name}
                  onChange={(e) => handlePositionChange(index, "name", e.target.value)}
                  placeholder="Full name"
                  className="form-input"
                  data-testid={`position-name-${index}`}
                />
              </div>
              {positions.length > 1 && (
                <button
                  onClick={() => removePosition(index)}
                  className="text-red-500 hover:text-red-700 p-2"
                  data-testid={`remove-position-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`director-${index}`}
                  checked={position.is_director}
                  onCheckedChange={(v) => handlePositionChange(index, "is_director", v)}
                  data-testid={`position-director-${index}`}
                />
                <Label htmlFor={`director-${index}`} className="cursor-pointer text-sm">Director</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`secretary-${index}`}
                  checked={position.is_secretary}
                  onCheckedChange={(v) => handlePositionChange(index, "is_secretary", v)}
                  data-testid={`position-secretary-${index}`}
                />
                <Label htmlFor={`secretary-${index}`} className="cursor-pointer text-sm">Secretary</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`shareholder-${index}`}
                  checked={position.is_shareholder}
                  onCheckedChange={(v) => handlePositionChange(index, "is_shareholder", v)}
                  data-testid={`position-shareholder-${index}`}
                />
                <Label htmlFor={`shareholder-${index}`} className="cursor-pointer text-sm">Public Officer/Shareholder</Label>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Registered Address Section */}
      <section className="form-card rounded">
        <h3 className="section-subheader">Registered Address</h3>
        
        <div className="form-grid form-grid-3">
          <div>
            <Label className="form-label">Unit No.</Label>
            <Input
              value={formData.company_borrower.registered_address.unit_no}
              onChange={(e) => handleAddressChange("unit_no", e.target.value)}
              placeholder="Unit"
              className="form-input"
              data-testid="address-unit-input"
            />
          </div>
          <div>
            <Label className="form-label">Street No.</Label>
            <Input
              value={formData.company_borrower.registered_address.street_no}
              onChange={(e) => handleAddressChange("street_no", e.target.value)}
              placeholder="Number"
              className="form-input"
              data-testid="address-street-no-input"
            />
          </div>
          <div>
            <Label className="form-label form-label-required">Street Name</Label>
            <Input
              value={formData.company_borrower.registered_address.street_name}
              onChange={(e) => handleAddressChange("street_name", e.target.value)}
              placeholder="Street name"
              className="form-input"
              data-testid="address-street-name-input"
            />
          </div>
          <div>
            <Label className="form-label form-label-required">Suburb</Label>
            <Input
              value={formData.company_borrower.registered_address.suburb}
              onChange={(e) => handleAddressChange("suburb", e.target.value)}
              placeholder="Suburb"
              className="form-input"
              data-testid="address-suburb-input"
            />
          </div>
          <div>
            <Label className="form-label form-label-required">State</Label>
            <Select
              value={formData.company_borrower.registered_address.state}
              onValueChange={(v) => handleAddressChange("state", v)}
            >
              <SelectTrigger className="form-input" data-testid="address-state-select">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {AUSTRALIAN_STATES.map((state) => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="form-label form-label-required">Postcode</Label>
            <Input
              value={formData.company_borrower.registered_address.postcode}
              onChange={(e) => handleAddressChange("postcode", e.target.value)}
              placeholder="XXXX"
              maxLength={4}
              className="form-input"
              data-testid="address-postcode-input"
            />
          </div>
        </div>
      </section>

      {/* General Borrower Enquiries Section */}
      <section className="form-card rounded">
        <h3 className="section-subheader">General Borrower Enquiries</h3>
        <p className="text-sm text-gray-500 mb-6">
          Please answer the following questions honestly. Where you have answered "Yes" to any questions, 
          please attach copies of any documents and/or reports in the Documents section.
        </p>

        <div className="space-y-4">
          {solvencyQuestions.map((q, index) => (
            <div 
              key={q.key} 
              className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded gap-4"
              data-testid={`solvency-question-${index}`}
            >
              <div className="flex items-start gap-3 flex-1">
                <span className="text-[#bd9a59] font-semibold">{index + 1}.</span>
                <p className="text-sm text-gray-700">{q.label}</p>
              </div>
              <RadioGroup
                value={formData.solvency_enquiries[q.key] ? "yes" : "no"}
                onValueChange={(v) => handleSolvencyChange(q.key, v === "yes")}
                className="flex gap-4 flex-shrink-0"
                data-testid={`solvency-${q.key}-radio`}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="yes" id={`${q.key}-yes`} />
                  <Label htmlFor={`${q.key}-yes`} className="cursor-pointer text-sm">Yes</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="no" id={`${q.key}-no`} />
                  <Label htmlFor={`${q.key}-no`} className="cursor-pointer text-sm">No</Label>
                </div>
              </RadioGroup>
            </div>
          ))}
        </div>

        {Object.values(formData.solvency_enquiries).some(v => v === true) && (
          <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800">
              You have answered "Yes" to one or more enquiry questions. Please ensure you upload 
              supporting documentation in the Documents section (Step 7).
            </p>
          </div>
        )}
      </section>
    </div>
  );
};
